import { ReactNode, UIEvent, useState } from "react";
import {
  Bars3BottomLeftIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import {
  Item,
  ItemTypes,
  ItemWithMetaData,
  ProfileAddedBy,
  Type,
} from "../api/Items";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { TrashOutlineIcon } from "../shared-components/icons";
import { SocialIcon } from "../utils/social-icon";
import ProfileAdd from "./ProfileAdd";
import {
  getProfilerTrustCardProps,
  ProfileCard,
  ProfilerReviewList,
  useProfileRemoveGuard,
} from "./ProfilerProfileCard";
import { ConfirmContent } from "../api/Items";
import { ProfileAddConfirmation } from "./profileConfirmEntities";
import { getProfileProvenance, sortProfilesByRecentAddOrder } from "./util";

type ProfilerProfileConfirmProps = {
  profileList: ItemWithMetaData[];
  suggestedProfiles: ItemWithMetaData[];
  isReadOnly: boolean;
  handleAddProfile?: (inputUrl: string) => void;
  handleDeleteProfile?: (itemUUID: string) => void;
  handleAddRating?: (
    itemUUID: string,
    confirmation?: ProfileAddConfirmation,
    existingContent?: ConfirmContent
  ) => void;
  pendingItemUUIDs?: ReadonlySet<string>;
  pendingRemoveToastUUIDs?: ReadonlySet<string>;
  recentlyAddedOrder?: readonly string[];
  errorMessage?: string;
  hasMoreReview?: boolean;
  loadMoreReview?: () => void;
  isLoadingMoreReview?: boolean;
};

const SectionContainer = ({
  title,
  count,
  description,
  headerActions,
  onScrollNearBottom,
  footer,
  children,
}: {
  title: string;
  count?: number;
  description?: string;
  headerActions?: ReactNode;
  onScrollNearBottom?: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) => {
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!onScrollNearBottom) return;
    const el = event.currentTarget;
    // Trigger a little before the very bottom so the next page is ready by the
    // time the user reaches it.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      onScrollNearBottom();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl font-semibold text-gray-900">{title}</span>
          {count !== undefined && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {count}
            </span>
          )}
        </div>
        {headerActions}
      </div>
      {description && (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      )}
      <div
        onScroll={handleScroll}
        className="mt-3 max-h-[950px] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-5 shadow-sm print:max-h-none print:overflow-visible"
      >
        {children}
        {footer}
      </div>
    </div>
  );
};

const ConfirmedRow = ({
  profile,
  isReadOnly,
  handleDeleteProfile,
  pendingItemUUIDs,
  pendingRemoveToastUUIDs,
}: {
  profile: ItemWithMetaData;
  isReadOnly: boolean;
  handleDeleteProfile?: (itemUUID: string) => void;
  pendingItemUUIDs?: ReadonlySet<string>;
  pendingRemoveToastUUIDs?: ReadonlySet<string>;
}) => {
  const url = profile.item.content?.url || "";
  const provenance = getProfileProvenance(profile.item.content?.added_by);
  const itemUUID = profile.item.external_id;
  const { canDelete, confirmRemove } = useProfileRemoveGuard(
    profile,
    handleDeleteProfile,
    { skipConfirmation: true }
  );
  const isPending = pendingItemUUIDs?.has(itemUUID) ?? false;
  const isRemoveToastPending = pendingRemoveToastUUIDs?.has(itemUUID) ?? false;

  if (isPending || isRemoveToastPending) {
    return null;
  }

  return (
    <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2.5">
      <SocialIcon url={url} />
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="truncate text-sm text-sky-600 hover:underline"
      >
        {url}
      </a>
      <span
        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${provenance.className}`}
      >
        {provenance.label}
      </span>
      {!isReadOnly && canDelete && (
        <button
          onClick={confirmRemove}
          className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-600"
          aria-label="Remove profile"
        >
          <TrashOutlineIcon />
        </button>
      )}
    </div>
  );
};

const ProfilerProfileConfirm = ({
  profileList,
  suggestedProfiles,
  isReadOnly,
  handleAddProfile,
  handleDeleteProfile,
  handleAddRating,
  pendingItemUUIDs,
  pendingRemoveToastUUIDs,
  recentlyAddedOrder = [],
  errorMessage,
  hasMoreReview,
  loadMoreReview,
  isLoadingMoreReview,
}: ProfilerProfileConfirmProps) => {
  const confirmedProfiles = filterConfirmedProfiles(profileList);
  const addedProfiles = sortProfilesByRecentAddOrder(
    filterAddedProfiles(profileList),
    recentlyAddedOrder
  );
  const visibleSuggestedProfiles = excludePendingRemoveToastProfiles(
    suggestedProfiles,
    pendingRemoveToastUUIDs
  );
  const visibleAddedProfiles = excludePendingRemoveToastProfiles(
    addedProfiles,
    pendingRemoveToastUUIDs
  );
  const [addedDetailsCompact, setAddedDetailsCompact] = useState(false);

  const addedColumnViewToggle = (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setAddedDetailsCompact(false)}
        aria-label="Show full profile details"
        aria-pressed={!addedDetailsCompact}
        title="Full view"
        className={`flex h-8 w-8 items-center justify-center rounded-md border ${
          !addedDetailsCompact
            ? "border-gray-400 bg-gray-100 text-gray-800"
            : "border-gray-200 bg-white text-gray-400 hover:text-gray-600"
        }`}
      >
        <RectangleStackIcon className="h-4 w-4" strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={() => setAddedDetailsCompact(true)}
        aria-label="Show compact profile details"
        aria-pressed={addedDetailsCompact}
        title="Compact view"
        className={`flex h-8 w-8 items-center justify-center rounded-md border ${
          addedDetailsCompact
            ? "border-gray-400 bg-gray-100 text-gray-800"
            : "border-gray-200 bg-white text-gray-400 hover:text-gray-600"
        }`}
      >
        <Bars3BottomLeftIcon className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <SectionContainer title="Confirmed">
        {!isReadOnly && (
          <div className="mb-4">
            <ProfileAdd
              handleAddProfile={(inputUrl) => handleAddProfile?.(inputUrl)}
            />
            {errorMessage && <ErrorMessageContainer message={errorMessage} />}
          </div>
        )}
        {confirmedProfiles.length === 0 ? (
          <div className="text-sm text-gray-500">No confirmed profiles</div>
        ) : (
          confirmedProfiles.map((profile) => (
            <ConfirmedRow
              key={profile.item.external_id}
              profile={profile}
              isReadOnly={isReadOnly}
              handleDeleteProfile={handleDeleteProfile}
              pendingItemUUIDs={pendingItemUUIDs}
              pendingRemoveToastUUIDs={pendingRemoveToastUUIDs}
            />
          ))
        )}
      </SectionContainer>
      <div className="grid grid-cols-2 gap-8">
        <SectionContainer
          title="Review"
          count={visibleSuggestedProfiles.length}
          onScrollNearBottom={hasMoreReview ? loadMoreReview : undefined}
          footer={
            isLoadingMoreReview ? (
              <div className="py-3 text-center text-sm text-gray-500">
                Loading more profiles…
              </div>
            ) : undefined
          }
        >
          <ProfilerReviewList
            suggestedProfiles={visibleSuggestedProfiles}
            isReadOnly={isReadOnly}
            handleAddRating={handleAddRating}
            handleDelete={handleDeleteProfile}
            pendingItemUUIDs={pendingItemUUIDs}
            pendingRemoveToastUUIDs={pendingRemoveToastUUIDs}
            skipRemoveConfirmation
          />
        </SectionContainer>
        <SectionContainer
          title="Added"
          count={visibleAddedProfiles.length}
          headerActions={addedColumnViewToggle}
        >
          {visibleAddedProfiles.length === 0 ? (
            <div className="text-sm text-gray-500">No added profiles</div>
          ) : (
            visibleAddedProfiles.map((profile) => (
              <ProfileCard
                key={profile.item.external_id}
                {...getProfilerTrustCardProps(profile)}
                profile={profile}
                isReadOnly={isReadOnly}
                handleDelete={handleDeleteProfile}
                pendingItemUUIDs={pendingItemUUIDs}
                pendingRemoveToastUUIDs={pendingRemoveToastUUIDs}
                skipRemoveConfirmation
                showAddToReport={false}
                detailsCompact={addedDetailsCompact}
              />
            ))
          )}
        </SectionContainer>
      </div>
    </div>
  );
};

// Confirmed = profiles in the report that came in pre-confirmed: client-provided,
// analyst-provided (typed into the Confirmed input), or auto-approved by the
// system. Anything reviewed in from the Review list belongs in "Added".
const filterConfirmedProfiles = (
  profileList: ItemWithMetaData[]
): ItemWithMetaData[] => {
  if (!profileList) return [];
  return profileList.filter((itemWithMetaData: ItemWithMetaData) => {
    const type: Type = itemWithMetaData.type;
    const item: Item = itemWithMetaData.item;
    const itemIsProfile = type.name === ItemTypes.SOCIAL_MEDIA_PROFILE;
    if (!itemIsProfile || !item.in_report) return false;
    const isClientAdded = item.content?.added_by === ProfileAddedBy.CLIENT;
    return isClientAdded || !item.is_reviewed;
  });
};

// Added = profiles the analyst pulled into the report from the Review list
// (reviewed/rated), excluding client-provided ones.
const filterAddedProfiles = (
  profileList: ItemWithMetaData[]
): ItemWithMetaData[] => {
  if (!profileList) return [];
  return profileList.filter((itemWithMetaData: ItemWithMetaData) => {
    const type: Type = itemWithMetaData.type;
    const item: Item = itemWithMetaData.item;
    const itemIsProfile = type.name === ItemTypes.SOCIAL_MEDIA_PROFILE;
    return (
      itemIsProfile &&
      item.in_report &&
      item.is_reviewed === true &&
      item.content?.added_by !== ProfileAddedBy.CLIENT
    );
  });
};

function excludePendingRemoveToastProfiles(
  profiles: ItemWithMetaData[],
  pendingRemoveToastUUIDs?: ReadonlySet<string>
): ItemWithMetaData[] {
  if (!pendingRemoveToastUUIDs?.size) {
    return profiles;
  }
  return profiles.filter(
    (profile) => !pendingRemoveToastUUIDs.has(profile.item.external_id)
  );
}

export default ProfilerProfileConfirm;
